import {
   Award,
   ChevronRight,
   Heart,
   Image as ImageIcon,
   PlayCircle,
   Share2,
   Shield,
   Star,
   Trophy,
   Users,
} from "lucide-react";
import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import DefaultPlayerImg from "../../image/default.png";
import "./PlayerTennisPublic.css";
import { useDomain } from "../../../../../../../context/DomainContext.jsx";


function PlayerTennisPublic({ playerData }) {
   const navigate = useNavigate();
   const [activeTab, setActiveTab] = useState("overview");
   const player = playerData?.player || playerData || {};
   const playerVideos =
      playerData?.videos ||
      player?.videos ||
      [];

   const fullName = `${player.firstName || ""} ${player.lastName || ""}`.trim();
   const activeEntry =
      playerData?.teamHistory?.find((entry) => entry.season?.active) ||
      playerData?.teamHistory?.[0];
   const [showAllMatches, setShowAllMatches] = useState(false);
   const teamName = player?.team?.name || activeEntry?.team?.name || "";
   const teamId = player?.team?.id || activeEntry?.team?.id || "";
   const { slug, isCustomDomain } = useDomain();

   const teamPath = isCustomDomain
      ? `/teams/${teamId}`
      : `/${slug}/teams/${teamId}`;
   const leagueName = activeEntry?.league?.name || "";
   const dominantHand =
      player?.attributeValues?.find(
         (item) => item?.attribute?.name === "Dominant Hand"
      )?.value || "";

   const playerImage =
      player.image ||
      player.profile_image ||
      player.profileImage ||
      player.photo ||
      DefaultPlayerImg;
   const playerId = player?.id;

   const playerTeamId =
      activeEntry?.team?.id || player?.team?.id;

   const allPlayerRacquetMatches = (playerData?.racquetMatches || []).filter(
      (match) =>
         match.home_player_1_id === playerId ||
         match.home_player_2_id === playerId ||
         match.away_player_1_id === playerId ||
         match.away_player_2_id === playerId
   );

   const playerRacquetMatches = allPlayerRacquetMatches.filter(
      (match) => match.status === "completed"
   );
   const getYouTubeVideoId = (url) => {
      if (!url) return null;

      try {
         const parsedUrl = new URL(url);

         if (parsedUrl.hostname.includes("youtube.com")) {
            const videoId = parsedUrl.searchParams.get("v");

            if (videoId) return videoId;

            if (parsedUrl.pathname.includes("/shorts/")) {
               return parsedUrl.pathname
                  .split("/shorts/")[1]
                  ?.split("/")[0];
            }
         }

         if (parsedUrl.hostname === "youtu.be") {
            return parsedUrl.pathname.replace("/", "");
         }

         return null;
      } catch {
         return null;
      }
   };
   const playerMatchWins = playerRacquetMatches.filter((match) => {
      const playerSide =
         match.home_player_1_id === playerId ||
            match.home_player_2_id === playerId
            ? "home"
            : "away";

      return match.winner_side === playerSide;
   }).length;

   const playerMatchLosses =
      playerRacquetMatches.length - playerMatchWins;

   const overallRecord =
      `${playerMatchWins} - ${playerMatchLosses}`;

   let setsWon = 0;

   playerRacquetMatches.forEach((match) => {
      const playerSide =
         match.home_player_1_id === playerId ||
            match.home_player_2_id === playerId
            ? "home"
            : "away";

      (match.scores || []).forEach((set) => {
         if (set.winner_side === playerSide) {
            setsWon += 1;
         }
      });
   });



   const winPercentage =
      playerRacquetMatches.length > 0
         ? Math.round((playerMatchWins / playerRacquetMatches.length) * 100)
         : 0;


   const playerRacquetStats = playerRacquetMatches.flatMap((match) =>
      (match.stats || []).filter((stat) => stat.player_id === playerId)
   );

   const aces = playerRacquetStats
      .filter((stat) => stat.stat?.abbreviation === "ACE")
      .reduce((sum, stat) => sum + Number(stat.value || 0), 0);

   const totalWinners = playerRacquetStats
      .filter((stat) => stat.stat?.abbreviation === "W")
      .reduce((sum, stat) => sum + Number(stat.value || 0), 0);

   let setsLost = 0;
   let gamesWon = 0;
   let gamesLost = 0;

   const singlesRecord = { wins: 0, losses: 0 };
   const doublesRecord = { wins: 0, losses: 0 };



   playerRacquetMatches.forEach((match) => {
      const playerSide =
         match.home_player_1_id === playerId || match.home_player_2_id === playerId
            ? "home"
            : "away";

      const isWin = match.winner_side === playerSide;

      const matchText = `${match.match_type || ""} ${match.match_label || ""}`.toLowerCase();

      let recordBucket = null;

      if (matchText.includes("single")) {
         recordBucket = singlesRecord;
      } else if (matchText.includes("double") || matchText.includes("mixed")) {
         recordBucket = doublesRecord;
      }

      if (recordBucket) {
         if (isWin) recordBucket.wins += 1;
         else recordBucket.losses += 1;
      }


      (match.scores || []).forEach((set) => {
         const homeScore = Number(set.home_score || 0);
         const awayScore = Number(set.away_score || 0);

         if (playerSide === "home") {
            gamesWon += homeScore;
            gamesLost += awayScore;
         } else {
            gamesWon += awayScore;
            gamesLost += homeScore;
         }

         if (set.winner_side && set.winner_side !== playerSide) {
            setsLost += 1;
         }
      });
   });

   const gameDifferential = gamesWon - gamesLost;


   const statRows = [
      ["Matches Played", playerRacquetMatches.length],
      ["Singles Record", `${singlesRecord.wins} - ${singlesRecord.losses}`],
      ["Doubles Record", `${doublesRecord.wins} - ${doublesRecord.losses}`],
      ["Sets Won / Lost", `${setsWon} - ${setsLost}`],
      ["Games Won / Lost", `${gamesWon} - ${gamesLost}`],
      ["Game Differential", gameDifferential > 0 ? `+${gameDifferential}` : gameDifferential],
   ];

const upcomingGames = (playerData?.gameStats || [])
   .map((entry) => entry.game)
   .filter(Boolean)
   .filter((game) => game.status !== "completed")
   .filter((game) => game.date || game.game_dateTime)
   .filter(
      (game, index, array) =>
         array.findIndex((item) => item.id === game.id) === index
   )
   .sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time || "00:00"}`);
      const dateB = new Date(`${b.date}T${b.time || "00:00"}`);
      return dateA - dateB;
   });

const nextGame = upcomingGames[0] || null;

const isIndividualNextGame = Boolean(
   nextGame?.home_player_id ||
   nextGame?.away_player_id ||
   nextGame?.home_partner_player_id ||
   nextGame?.away_partner_player_id
);

const getGamePlayer = (entry) => {
   return entry?.player || entry || null;
};

const getGamePlayerName = (entry) => {
   const gamePlayer = getGamePlayer(entry);

   if (!gamePlayer) return "";

   return `${gamePlayer.firstName || ""} ${
      gamePlayer.lastName || ""
   }`.trim();
};

const homeGamePlayers = [
   getGamePlayer(nextGame?.home_player),
   getGamePlayer(nextGame?.home_partner_player),
].filter(Boolean);

const awayGamePlayers = [
   getGamePlayer(nextGame?.away_player),
   getGamePlayer(nextGame?.away_partner_player),
].filter(Boolean);

const playerIsOnHomeSide = homeGamePlayers.some(
   (gamePlayer) => Number(gamePlayer?.id) === Number(playerId)
);

const playerIsOnAwaySide = awayGamePlayers.some(
   (gamePlayer) => Number(gamePlayer?.id) === Number(playerId)
);

let nextOpponentText = "";
let nextPartnerText = "";

if (isIndividualNextGame) {
   const playerSide = playerIsOnHomeSide
      ? homeGamePlayers
      : playerIsOnAwaySide
         ? awayGamePlayers
         : [];

   const opponentSide = playerIsOnHomeSide
      ? awayGamePlayers
      : playerIsOnAwaySide
         ? homeGamePlayers
         : [];

   const partner = playerSide.find(
      (gamePlayer) => Number(gamePlayer?.id) !== Number(playerId)
   );

   nextPartnerText = partner
      ? getGamePlayerName(partner)
      : "";

   nextOpponentText = opponentSide
      .map((gamePlayer) => getGamePlayerName(gamePlayer))
      .filter(Boolean)
      .join(" / ");
}

const isNextHome =
   nextGame?.home_team?.team_id === playerTeamId;

const teamOpponentName = isNextHome
   ? nextGame?.away_team?.team?.name
   : nextGame?.home_team?.team?.name;

const nextMatchDate = nextGame?.date
   ? new Date(
      `${nextGame.date}T${nextGame.time || "00:00"}`
   ).toLocaleString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      ...(nextGame.time
         ? {
            hour: "numeric",
            minute: "2-digit",
         }
         : {}),
   })
   : "";

   const gamesById = {};

   (playerData?.gameStats || []).forEach((entry) => {
      if (entry?.game?.id) {
         gamesById[entry.game.id] = entry.game;
      }
   });

   const formatMatchDate = (game) => {
      if (!game?.date) return "—";

      return new Date(`${game.date}T00:00:00`).toLocaleDateString([], {
         month: "numeric",
         day: "numeric",
         year: "2-digit",
      });
   };


   const racquetMatchRows = allPlayerRacquetMatches.map((match) => {
      const playerSide =
         match.home_player_1_id === playerId ||
            match.home_player_2_id === playerId
            ? "home"
            : "away";

      const game = gamesById[match.game_id];

      const result =
         match.status !== "completed"
            ? "Upcoming"
            : match.winner_side === playerSide
               ? "Win"
               : "Loss";

      const partner =
         playerSide === "home"
            ? match.home_player_1_id === playerId
               ? match.homePlayer2
               : match.homePlayer1
            : match.away_player_1_id === playerId
               ? match.awayPlayer2
               : match.awayPlayer1;

      const partnerName = partner
         ? `${partner.firstName || ""} ${partner.lastName || ""}`.trim()
         : "";

      const opponentNames =
         playerSide === "home"
            ? [match.awayPlayer1, match.awayPlayer2]
            : [match.homePlayer1, match.homePlayer2];

      const opponentText = opponentNames
         .filter(Boolean)
         .map((p) =>
            `${p.firstName || ""} ${p.lastName || ""}`.trim()
         )
         .filter(Boolean)
         .join(" / ");

      const partnerOpponent =
         match.match_type === "doubles" && partnerName
            ? `w/ ${partnerName} vs ${opponentText || "Opponent TBD"}`
            : `vs ${opponentText || "Opponent TBD"}`;

      const scoreText = (match.scores || [])
         .map((set) => `${set.home_score}-${set.away_score}`)
         .join(", ");

      return {
         id: match.id,
         gameId: match.game_id,
         date: formatMatchDate(game),
         type: match.match_type,
         teamLine: match.match_label,
         partnerOpponent,
         result,
         score:
            match.status === "completed"
               ? scoreText || "—"
               : "—",
         rawMatch: match,
         game,
      };
   });
   const upcomingGameRows = upcomingGames
      .filter(
         (game) =>
            !allPlayerRacquetMatches.some(
               (match) => Number(match.game_id) === Number(game.id)
            )
      )
      .map((game) => {
         const homePlayers = [
            game?.home_player?.player,
            game?.home_partner_player?.player,
         ].filter(Boolean);

         const awayPlayers = [
            game?.away_player?.player,
            game?.away_partner_player?.player,
         ].filter(Boolean);

         const playerIsHome = homePlayers.some(
            (gamePlayer) => Number(gamePlayer?.id) === Number(playerId)
         );

         const playerIsAway = awayPlayers.some(
            (gamePlayer) => Number(gamePlayer?.id) === Number(playerId)
         );

         const playerSide = playerIsHome
            ? homePlayers
            : playerIsAway
               ? awayPlayers
               : [];

         const opponentSide = playerIsHome
            ? awayPlayers
            : playerIsAway
               ? homePlayers
               : [];

         const partner = playerSide.find(
            (gamePlayer) => Number(gamePlayer?.id) !== Number(playerId)
         );

         const partnerName = partner
            ? `${partner.firstName || ""} ${partner.lastName || ""}`.trim()
            : "";

         const opponentText = opponentSide
            .map(
               (gamePlayer) =>
                  `${gamePlayer.firstName || ""} ${gamePlayer.lastName || ""
                     }`.trim()
            )
            .filter(Boolean)
            .join(" / ");

         const isDoubles =
            game.match_format === "doubles" ||
            Boolean(
               game.home_partner_player_id ||
               game.away_partner_player_id
            );

         const partnerOpponent =
            isDoubles && partnerName
               ? `w/ ${partnerName} vs ${opponentText || "Match Scheduled"
               }`
               : opponentText
                  ? `vs ${opponentText}`
                  : "Match Scheduled";

         return {
            id: `game-${game.id}`,
            gameId: game.id,
            date: formatMatchDate(game),
            type: isDoubles ? "doubles" : "singles",
            teamLine: "",
            partnerOpponent,
            result: "Upcoming",
            score: "—",
            rawMatch: null,
            game,
         };
      });

   const matchHistoryRows = [
      ...racquetMatchRows,
      ...upcomingGameRows,
   ];

   const hasMatchLine = matchHistoryRows.some(
      (match) => String(match.teamLine || "").trim()
   );
   const formatMatchType = (type) => {
      if (!type) return "—";

      return String(type)
         .replace(/_/g, " ")
         .split(" ")
         .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
         .join(" ");
   };


   const buildMatchStatRows = (rawMatch) => {
      const matchStats = Array.isArray(rawMatch?.stats)
         ? rawMatch.stats
         : [];

      if (matchStats.length === 0) return [];

      const homePlayerIds = [
         rawMatch?.home_player_1_id,
         rawMatch?.home_player_2_id,
      ].filter(Boolean);

      const awayPlayerIds = [
         rawMatch?.away_player_1_id,
         rawMatch?.away_player_2_id,
      ].filter(Boolean);

      const groupedStats = {};

      matchStats.forEach((entry) => {
         const statId = entry?.stat_id;
         const statDefinition = entry?.stat;
         const playerId = entry?.player_id;

         if (!statId || !statDefinition) return;
         if (statDefinition.is_visible === false) return;

         if (!groupedStats[statId]) {
            groupedStats[statId] = {
               id: statId,
               name: statDefinition.name || "Stat",
               abbreviation: statDefinition.abbreviation || "",
               displayOrder: statDefinition.display_order ?? 999,
               homeValue: 0,
               awayValue: 0,
            };
         }

         const numericValue = Number(entry.value || 0);

         if (homePlayerIds.includes(playerId)) {
            groupedStats[statId].homeValue += numericValue;
         }

         if (awayPlayerIds.includes(playerId)) {
            groupedStats[statId].awayValue += numericValue;
         }
      });

      return Object.values(groupedStats).sort(
         (a, b) => a.displayOrder - b.displayOrder
      );
   };
   const openGameWithMatch = (matchRow) => {
      if (!matchRow?.gameId || !matchRow?.id) return;

      const gamePath = isCustomDomain
         ? `/games/${matchRow.gameId}?match=${matchRow.id}`
         : `/${slug}/games/${matchRow.gameId}?match=${matchRow.id}`;

      navigate(gamePath);
   };





   return (
      <div className="tennis-profile-page">

         <main className="tennis-shell">

            <section className="tennis-profile-card">
               <div className="tennis-profile-main">
                  <div className="tennis-photo-wrap">
                     <img src={playerImage} alt={fullName || "Player"} />


                  </div>

                  <div className="tennis-info-main">
                     <div className="tennis-name-row">
                        <div>
                           <div className="tennis-name-inline">
                              <h1>{fullName || "Player Name"}</h1>

                              <div className="tennis-meta">
                                 {dominantHand && (
                                    <span>🎾 {dominantHand}-Handed</span>
                                 )}
                              </div>
                           </div>

                           {/* <div className="tennis-meta">
                              {leagueName && <span>{leagueName}</span>}
                           </div> */}

                           {teamName && teamId && (
                              <div className="tennis-team-meta">
                                 Team:{" "}
                                 <NavLink
                                    to={teamPath}
                                    className="tennis-team-link"
                                 >
                                    {teamName}
                                 </NavLink>
                              </div>
                           )}
                        </div>

                        <div className="tennis-actions">

                           {/* <button className="share">
                              <Share2 size={16} />
                              Share Profile
                           </button> */}
                        </div>
                     </div>

                     <div className="tennis-quick-stats">
                        <div>
                           <p>Overall Record</p>
                           <strong>{overallRecord}</strong>
                        </div>

                        {/* <div>
                           <p>Team Points</p>
                           <strong>5</strong>
                        </div> */}

                        <div>
                           <p>Sets Won</p>
                           <strong>{setsWon}</strong>
                        </div>

                        <div>
                           <p>Win %</p>
                           <strong>{winPercentage}%</strong>
                        </div>

                        <div>
                           <p>Aces</p>
                           <strong>{aces}</strong>
                        </div>

                        <div>
                           <p>Total Winners</p>
                           <strong>{totalWinners}</strong>
                        </div>
                     </div>
                  </div>
               </div>

               <div className="tennis-profile-side">
                  <div className="next-match-mini">
                     <p>
                        {isIndividualNextGame
                           ? "NEXT MATCH"
                           : "NEXT TEAM MATCH"}
                     </p>

                     {nextGame ? (
                        <>
                           {isIndividualNextGame ? (
                              <>
                                 {nextPartnerText && (
                                    <small>
                                       w/ {nextPartnerText}
                                    </small>
                                 )}

                                 <h3>
                                    {nextOpponentText
                                       ? `vs ${nextOpponentText}`
                                       : "Match Scheduled"}
                                 </h3>
                              </>
                           ) : (
                              <h3>
                                 {teamOpponentName
                                    ? `vs ${teamOpponentName}`
                                    : "Match Scheduled"}
                              </h3>
                           )}

                           <span>{nextMatchDate}</span>

                           <small>
                              {nextGame.location ||
                                 "Location TBA"}
                           </small>

                           <button
                              type="button"
                              onClick={() => {
                                 const gamePath = isCustomDomain
                                    ? `/games/${nextGame.id}`
                                    : `/${slug}/games/${nextGame.id}`;

                                 navigate(gamePath);
                              }}
                           >
                              View Game
                           </button>
                        </>
                     ) : (
                        <>
                           <h3>No upcoming match</h3>
                           <span>Schedule TBD</span>
                           <small>Check back later</small>
                        </>
                     )}
                  </div>
               
                  {/* 
                  <div className="best-match-mini">
                     <Star size={22} />
                     <div>
                        <p>BEST MATCH</p>
                        <h3>6-1, 6-3 Win</h3>
                        <span>Jun 18 vs Mike Chen</span>
                     </div>
                  </div> */}
               </div>
            </section>

            <div className="tennis-tabs">
               <button
                  className={activeTab === "overview" ? "active" : ""}
                  onClick={() => setActiveTab("overview")}
               >
                  Overview
               </button>

            </div>

            {activeTab === "overview" && (
               <>
                  <section className="tennis-overview-grid">
                     <div className="tennis-panel match-history-panel">
                        <div className="panel-header">
                           <h3>Matches</h3>
                           {matchHistoryRows.length > 5 && (
                              <button onClick={() => setShowAllMatches((prev) => !prev)}>
                                 {showAllMatches ? "Show Less" : "View All Matches"}
                                 <ChevronRight size={15} />
                              </button>
                           )}
                        </div>

                        <div className="table-scroll">
                           <table>
                              <thead>
                                 <tr>
                                    <th>Date</th>
                                    <th>Match Type</th>
                                    {hasMatchLine && <th>Line</th>}
                                    <th>Partner / Opponent</th>
                                    <th>Result</th>
                                    <th>Score</th>
                                 </tr>
                              </thead>

                              <tbody>
                                 {(showAllMatches ? matchHistoryRows : matchHistoryRows.slice(0, 5)).map(
                                    (match, index) => (

                                    <tr
                                       key={match.id || index}
                                       className="tennis-match-history-row"
                                       onClick={() => openGameWithMatch(match)}
                                       onKeyDown={(event) => {
                                          if (event.key === "Enter" || event.key === " ") {
                                             event.preventDefault();
                                             openGameWithMatch(match);
                                          }
                                       }}
                                       role="link"
                                       tabIndex={0}
                                    >
                                          <td>{match.date}</td>
                                          <td>{formatMatchType(match.type)}</td>
                                          {hasMatchLine && <td>{match.teamLine}</td>}
                                          <td>{match.partnerOpponent}</td>
                                       <td>
                                          <span
                                             className={
                                                match.result === "Win"
                                                   ? "result-pill win"
                                                   : match.result === "Loss"
                                                      ? "result-pill loss"
                                                      : "result-pill upcoming"
                                             }
                                          >
                                             {match.result}
                                          </span>
                                       </td>
                                       <td>{match.score}</td>
                                    </tr>
                                 ))}
                              </tbody>
                           </table>
                        </div>
                     </div>

                     <aside className="tennis-panel season-stats-panel">
                        <div className="panel-header">
                           <h3>Season Stats</h3>
                        </div>

                        <div className="season-stat-list">
                           {statRows.slice(0, 8).map(([label, value]) => (
                              <div key={label}>
                                 <span>{label}</span>
                                 <strong>{value}</strong>
                              </div>
                           ))}
                        </div>
                     </aside>
                  </section>
                  <section className="tennis-bottom-grid media-only-grid">
                     <div className="tennis-panel media-highlights-panel">
                        
                        <div className="panel-header">
                           <h3>Media Highlights</h3>
                        </div>

                        {playerVideos.length > 0 ? (
                           <div className="media-highlight-grid">
                              {playerVideos.map((video) => {
                                 const videoId = getYouTubeVideoId(video.video_url);

                                 const thumbnail = videoId
                                    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                                    : null;

                                 return (
                                    <a
                                       key={video.id}
                                       className="media-highlight-card"
                                       href={video.video_url}
                                       target="_blank"
                                       rel="noreferrer"
                                    >
                                       <div className="media-thumb">
                                          {thumbnail ? (
                                             <img
                                                src={thumbnail}
                                                alt={video.title || "Player highlight"}
                                             />
                                          ) : (
                                             <div className="media-empty-small">
                                                <PlayCircle size={34} />
                                             </div>
                                          )}

                                          <span className="media-type youtube">
                                             <PlayCircle size={15} />
                                          </span>

                                          <div className="play-overlay">
                                             <PlayCircle size={42} />
                                          </div>
                                       </div>

                                       <div className="media-info">
                                          <strong>
                                             {video.title || "Player Highlight"}
                                          </strong>

                                          {video.description && (
                                             <p>{video.description}</p>
                                          )}
                                       </div>
                                    </a>
                                 );
                              })}
                           </div>
                        ) : (
                           <div className="media-empty-small">
                              <PlayCircle size={34} />
                              <strong>No media highlights yet</strong>
                              <p>
                                 Player videos will appear here once added.
                              </p>
                           </div>
                        )}
                     </div>
                  </section>
               </>
            )}

            {activeTab === "history" && (
               <section className="tennis-panel full-panel">
                  <div className="panel-header">
                     <h3>Full Match History</h3>
                  </div>

                  <div className="table-scroll">
                     <table>
                        <thead>
                           <tr>
                              <th>Date</th>
                              <th>Match Type</th>
                              <th>Line</th>
                              <th>Partner / Opponent</th>
                              <th>Result</th>
                              <th>Score</th>
                           </tr>
                        </thead>

                        <tbody>
                           {matchHistoryRows.map((match, index) => (
                              <tr
                                 key={match.id || index}
                                 className="tennis-match-history-row"
                                 onClick={() => openGameWithMatch(match)}
                                 onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                       event.preventDefault();
                                       openGameWithMatch(match);
                                    }
                                 }}
                                 role="link"
                                 tabIndex={0}
                              >

                                 <td>{match.date}</td>
                                 <td>{formatMatchType(match.type)}</td>
                                 <td>{match.teamLine}</td>
                                 <td>{match.partnerOpponent}</td>
                                 <td>{match.result}</td>
                                 <td>{match.score}</td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               </section>
            )}






         </main>
      </div>
   );
}

export default PlayerTennisPublic;